// Attaches X-Cv2-Account header to same-origin API requests so the server scopes data to the
// active account. Tab-scoped (module variable, not localStorage) to allow multiple accounts in
// parallel tabs.

import type { Path, To } from "react-router";

const ACCOUNT_HEADER = "X-Cv2-Account";

// localStorage key — remembers the last-used account across sessions. Only a seed; the
// interceptor module variable is the live per-tab source of truth.
export const ACCOUNT_STORAGE_KEY = "cv2_active_account_id";

// URL param that carries the active account for reloads and shared links.
export const ACCOUNT_ID_PARAM = "aid";

/** Returns a copy of a navigation target with `?aid=<accountId>` merged in. */
export function withAccountId(to: To, accountId: string): To {
	if (typeof to === "string") {
		const hashIndex = to.indexOf("#");
		const hash = hashIndex >= 0 ? to.slice(hashIndex) : "";
		const withoutHash = hashIndex >= 0 ? to.slice(0, hashIndex) : to;
		const [pathname, search = ""] = withoutHash.split("?");
		const params = new URLSearchParams(search);
		params.set(ACCOUNT_ID_PARAM, accountId);
		return `${pathname}?${params.toString()}${hash}`;
	}

	const partial = to as Partial<Path>;
	const params = new URLSearchParams(partial.search ?? "");
	params.set(ACCOUNT_ID_PARAM, accountId);
	return { ...partial, search: `?${params.toString()}` };
}

let activeAccountId: string | null = null;
let installed = false;

/** Set (or clear) the account sent on subsequent API requests for this tab. */
export function setActiveAccountId(id: string | null): void {
	activeAccountId = id;
}

export function getActiveAccountId(): string | null {
	return activeAccountId;
}

/** Wraps window.fetch to inject X-Cv2-Account on same-origin API requests. Idempotent. */
export function installAccountHeaderInterceptor(): void {
	if (installed) {
		return;
	}
	installed = true;

	const original = window.fetch.bind(window);
	window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
		if (activeAccountId && isSameOriginApi(input) && !isSsoidScopedEndpoint(input)) {
			const headers = new Headers(
				init?.headers ?? (input instanceof Request ? input.headers : undefined)
			);
			headers.set(ACCOUNT_HEADER, activeAccountId);
			return original(input, { ...init, headers });
		}
		return original(input, init);
	};
}

/**
 * Seeds activeAccountId from `?aid=` (hash fragment) or localStorage before the first render.
 * AccountProvider validates ownership after api/Accounts resolves and self-heals if needed.
 */
export function seedAccountIdFromUrlOrStorage(): void {
	// HashRouter puts query params inside location.hash, not location.search.
	const hash = window.location.hash;
	const questionMarkIndex = hash.indexOf("?");
	const fromUrl =
		questionMarkIndex >= 0
			? new URLSearchParams(hash.slice(questionMarkIndex + 1)).get(ACCOUNT_ID_PARAM)
			: null;
	const seed = fromUrl ?? localStorage.getItem(ACCOUNT_STORAGE_KEY);
	if (seed) {
		setActiveAccountId(seed);
	}
}

function isSameOriginApi(input: RequestInfo | URL): boolean {
	try {
		const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
		const url = new URL(raw, window.location.origin);
		return url.origin === window.location.origin && url.pathname.toLowerCase().includes("/api/");
	} catch {
		return false;
	}
}

// SSOID-scoped endpoints (keyed off the `ssoid` claim, not the active account) must not receive an
// account header: the middleware's IDOR guard 403s a header naming a pending or foreign account, and
// a pending user's active account is exactly such a pending company. The account list, My Profile,
// request-access and create-company all fall here. /api/Accounts/ActivePermissions IS account-scoped
// and must keep the header, so we match specific paths rather than an /api/accounts prefix.
function isSsoidScopedEndpoint(input: RequestInfo | URL): boolean {
	try {
		const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
		const path = new URL(raw, window.location.origin).pathname.replace(/\/+$/, "").toLowerCase();
		return (
			path.endsWith("/api/accounts") ||
			path.endsWith("/api/accounts/requestaccess") ||
			path.endsWith("/api/accounts/createcompanyaccount") ||
			path.endsWith("/api/myprofile")
		);
	} catch {
		return false;
	}
}
